import { useEffect, useState, useRef } from "react";
import { api, apiError, API_BASE, getSessionTokenForTest } from "@/lib/api";
import { fmtINR, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Camera, Upload as UploadIcon, Check, AlertTriangle, XCircle, Eye, Scan, X } from "lucide-react";
import { slotLabel } from "@/lib/labels";
import ProofChecklist from "@/components/ProofChecklist";

const SHIFTS_3 = ["SC-01", "SC-02", "SC-03"];
const SHIFTS_2 = ["SC-D", "SC-N"];

export default function UploadInbox() {
  const [date, setDate] = useState(todayISO());
  const [shiftCode, setShiftCode] = useState("SC-01");
  const [files, setFiles] = useState([]);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shiftMode, setShiftMode] = useState("3-shift");
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [checklistSignal, setChecklistSignal] = useState(0);
  // Phase-3.5: dedicated ET-slip scan modal — parses per-nozzle VTOT.
  const [etScan, setEtScan] = useState(null);   // { file_id, filename, result, role, injecting }
  const [etBusy, setEtBusy] = useState(false);
  const inputRef = useRef(null);

  // Phase-3.8: one-tap inject VTOT readings into the draft shift entry.
  async function injectEtReadings(scan, role) {
    if (!scan?.result?.nozzles?.length) return;
    const mapped = scan.result.nozzles.filter((n) => n.matched_nozzle_id && n.vtot_l != null);
    if (!mapped.length) {
      toast.error("None of the scanned nozzles could be mapped to a nozzle in our master data.");
      return;
    }
    setEtScan((s) => ({ ...s, injecting: true }));
    try {
      const r = await api.post("/meter_ocr/apply_readings", {
        date, shift_code: shiftCode, role,
        readings: mapped.map((n) => ({ nozzle_id: n.matched_nozzle_id, vtot_l: n.vtot_l })),
      });
      toast.success(`Injected ${r.data.updated} ${role === "open" ? "OPENING" : "CLOSING"} readings into today's ${shiftCode} draft.`);
      setEtScan((s) => ({ ...s, injected: r.data }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEtScan((s) => s && ({ ...s, injecting: false }));
    }
  }

  useEffect(() => {
    (async () => {
      const cfg = await api.get("/masters/config");
      setShiftMode(cfg.data?.SHIFT_MODE || "3-shift");
    })();
  }, []);

  const shiftOptions = shiftMode === "2-shift" ? SHIFTS_2 : SHIFTS_3;

  const reload = async () => {
    try {
      const [f, s] = await Promise.all([
        api.get("/uploads/", { params: { date, shift_code: shiftCode } }),
        api.get("/uploads/slots", { params: { date, shift_code: shiftCode } }),
      ]);
      setFiles(f.data || []);
      setSlots(s.data || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { reload(); }, [date, shiftCode]); // eslint-disable-line

  async function upload(fileList) {
    setBusy(true);
    for (const f of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("date", date);
      fd.append("shift_code", shiftCode);
      try {
        await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) { toast.error(apiError(e)); }
    }
    setBusy(false);
    toast.success("Uploaded");
    reload();
    setChecklistSignal((n) => n + 1);
  }

  async function replaceFile(oldFileId, file) {
    // Retake: upload a fresh photo. If the old file was bound to a checklist
    // slot, the new one inherits the same slot (via /uploads/checklist/bind).
    const oldFile = files.find((x) => x.file_id === oldFileId) || {};
    const fd = new FormData();
    fd.append("file", file);
    if (date) fd.append("date", date);
    if (shiftCode) fd.append("shift_code", shiftCode);
    fd.append("note", `Retake of ${oldFile.original_name || oldFileId}`);
    try {
      const r = await api.post("/uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (oldFile.checklist_key) {
        await api.post("/uploads/checklist/bind", {
          date, shift_code: shiftCode, key: oldFile.checklist_key, file_id: r.data.file_id,
        });
      }
      toast.success("Retake attached — thank you.");
      reload();
      setChecklistSignal((n) => n + 1);
    } catch (e) { toast.error(apiError(e)); }
  }

  // Handbook rule (VAS handbook §"Opening & Closing Receipts"): the ET slip
  // lists all 4 nozzles of a DU with both ATOT (₹) and VTOT (litres). VTOT is
  // what we want. This scanner posts the file straight to Gemini and shows
  // the parsed per-nozzle table.
  async function scanEtReceipt(f) {
    setEtBusy(true);
    setEtScan({ file_id: f.file_id, filename: f.original_name || f.file_id, result: null });
    try {
      // Fetch the file bytes from storage so we don't need to re-upload
      const resp = await fetch(`${API_BASE}/uploads/file/${f.file_id}`, {
        credentials: "include",
        headers: getSessionTokenForTest() ? { Authorization: `Bearer ${getSessionTokenForTest()}` } : {},
      });
      if (!resp.ok) throw new Error(`Fetch file failed (${resp.status})`);
      const blob = await resp.blob();
      const fd = new FormData();
      fd.append("file", new File([blob], f.original_name || `${f.file_id}.jpg`, { type: blob.type || "image/jpeg" }));
      const r = await api.post("/meter_ocr/read_et_receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEtScan({ file_id: f.file_id, filename: f.original_name || f.file_id, result: r.data });
      if (r.data.status === "OK") {
        toast.success(`Read ${r.data.nozzles?.length || 0} nozzles from the ET slip`);
      } else if (r.data.status === "UNREADABLE") {
        toast.warning("Could not read the slip — please retake the photo");
      } else {
        toast.error(`OCR ${r.data.status}: ${r.data.error || ""}`.trim());
      }
    } catch (e) {
      toast.error(apiError(e));
      setEtScan(null);
    } finally {
      setEtBusy(false);
    }
  }

  async function viewFile(fileId) {
    setSelectedFileId(fileId);
    try {
      const r = await fetch(`${API_BASE}/uploads/file/${fileId}`, {
        credentials: "include",
        headers: getSessionTokenForTest() ? { Authorization: `Bearer ${getSessionTokenForTest()}` } : {},
      });
      if (!r.ok) throw new Error(`Server returned ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      // v17.11 (2026-08-19) bug 3.b: open in a new tab so PDFs render in the
      // browser's built-in PDF viewer instead of trying to <img> a PDF and
      // showing nothing. Images still render fine in the new tab.
      window.open(url, "_blank", "noopener");
      setPreviewUrl(url);
    } catch (e) { toast.error(`Couldn't open file: ${apiError(e)}`); }
  }

  const totalFiles = files.length;

  return (
    <div className="space-y-4" data-testid="upload-inbox-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Upload</h1>
        <p className="text-sm text-slate-600 mt-1">
          Dump everything here — nozzle receipts, payment screenshots, signed debtor slips, adjustment photos. The right-side checklist tells you what&rsquo;s still missing for this date + shift, grouped by kind.
        </p>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="block h-11 border border-slate-300 rounded-sm px-3 bg-white" data-testid="ui-date" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Shift</label>
          <div className="flex gap-1 mt-0.5">
            {shiftOptions.map((s) => (
              <button key={s} data-testid={`ui-shift-${s}`} type="button" onClick={() => setShiftCode(s)}
                      className={"px-3 h-11 border-2 font-bold text-sm rounded-sm " + (shiftCode === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300")}>{s}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Proof Checklist — 22 sales + 9 payment + dynamic debtor/adjustment
          rows.  Renders immediately when date+shift is picked, independent
          of whether a Shift Entry has been submitted.  See Message 675
          spec + /app/backend/app/checklist.py. */}
      <ProofChecklist
        date={date}
        shiftCode={shiftCode}
        files={files}
        onBindRequest={() => reload()}
        refreshSignal={checklistSignal}
      />

      <div className="grid grid-cols-1 gap-3">
        {/* Big drop zone — spans full width now that the checklist has moved above */}
        <div>
          <Card
            data-testid="upload-dropzone"
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
            onDragOver={(e) => e.preventDefault()}
            className="border-4 border-dashed border-slate-400 rounded-sm bg-white p-6 md:p-10 text-center cursor-pointer"
            onClick={() => inputRef.current?.click()}
          >
            <UploadIcon size={48} className="mx-auto text-slate-500" />
            <div className="text-base font-bold text-slate-800 mt-3">Upload everything here</div>
            <div className="text-xs text-slate-600 mt-1">Tap or drop photos & PDFs. Compressed &lt; 25 MB. After uploading, attach each file to a checklist slot above.</div>
            <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" hidden
                   onChange={(e) => upload(e.target.files)} data-testid="upload-file-input" />
            <Button type="button" className="mt-3 bg-slate-900 hover:bg-slate-800 text-white" disabled={busy}>
              <Camera size={16} className="mr-1" /> Choose files
            </Button>
          </Card>

          {/* Uploaded files */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2">
            {files.map((f) => (
              <FileCard key={f.file_id} file={f}
                        date={date} shiftCode={shiftCode}
                        onView={() => viewFile(f.file_id)}
                        onRetake={(nf) => replaceFile(f.file_id, nf)}
                        onChanged={() => { reload(); setChecklistSignal((n) => n + 1); }} />
            ))}
          </div>

          {previewUrl && (
            <div className="mt-4 border-2 border-slate-300 rounded-sm bg-white p-2">
              <div className="text-xs font-bold text-slate-700 mb-2">Preview</div>
              <img src={previewUrl} alt="preview" className="max-h-96 mx-auto" />
            </div>
          )}
        </div>
      </div>

      {/* ---- ET (Electronic Totalizer) scan modal ------------------------ */}
      {etScan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" data-testid="et-scan-modal">
          <div className="bg-white rounded-sm border-2 border-slate-900 max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-3 border-b border-slate-200">
              <div>
                <h3 className="font-bold text-slate-900 text-lg">Electronic Totalizer — parsed nozzles</h3>
                <div className="text-xs text-slate-500 mt-0.5 truncate max-w-[400px]">{etScan.filename}</div>
              </div>
              <button onClick={() => setEtScan(null)} className="p-1 hover:bg-slate-100 rounded" data-testid="et-scan-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              {etBusy && (
                <div className="text-center text-sm text-slate-600 py-8">
                  <div className="animate-pulse">Reading the slip via Gemini…</div>
                  <div className="text-xs text-slate-400 mt-1">Usually takes 3–8 seconds.</div>
                </div>
              )}

              {!etBusy && etScan.result && etScan.result.status === "OK" && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                      <div className="text-slate-500 uppercase tracking-wider font-bold">FIP No.</div>
                      <div className="font-mono font-bold">{etScan.result.fip_no || "—"}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                      <div className="text-slate-500 uppercase tracking-wider font-bold">DU Serial</div>
                      <div className="font-mono font-bold">{etScan.result.du_serial || "—"}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                      <div className="text-slate-500 uppercase tracking-wider font-bold">Date · Time</div>
                      <div className="font-mono">{etScan.result.date || "—"} · {etScan.result.time || "—"}</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                      <div className="text-slate-500 uppercase tracking-wider font-bold">Confidence</div>
                      <div className="font-bold">{Math.round((etScan.result.confidence || 0) * 100)}%</div>
                    </div>
                  </div>

                  <div className="border-2 border-slate-300 rounded-sm overflow-hidden">
                    <table className="dense-table w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left px-2 py-1">ET #</th>
                          <th className="text-left px-2 py-1">→ Our nozzle</th>
                          <th className="text-right px-2 py-1 font-bold text-emerald-800">VTOT (L)</th>
                          <th className="text-right px-2 py-1">Previous close</th>
                          <th className="text-left px-2 py-1">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(etScan.result.nozzles || []).map((n) => (
                          <tr key={n.nozzle_no} className="border-t border-slate-200" data-testid={`et-row-${n.nozzle_no}`}>
                            <td className="px-2 py-1 font-bold">#{n.nozzle_no}</td>
                            <td className="px-2 py-1">
                              {n.matched_nozzle_label ? (
                                <div>
                                  <div className="font-bold text-slate-900 text-sm">{n.matched_nozzle_label}</div>
                                  <div className="text-[10px] text-slate-500 font-mono">{n.matched_fuel}</div>
                                </div>
                              ) : (
                                <span className="text-red-600 italic text-xs">no match — check DU serial</span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-right font-mono font-bold text-emerald-800 num">
                              {n.vtot_l != null ? n.vtot_l.toFixed(2) : "—"}
                            </td>
                            <td className="px-2 py-1 text-right font-mono text-xs text-slate-600 num">
                              {n.previous_closing_l != null ? n.previous_closing_l.toFixed(2) : <span className="text-slate-400">first shift</span>}
                            </td>
                            <td className="px-2 py-1">
                              {n.variance_severity === "HARD" && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-600 text-white rounded-sm" title={n.variance_message}>
                                  ⚠ {n.variance_pct != null ? `${n.variance_pct}%` : "check"}
                                </span>
                              )}
                              {n.variance_severity === "MED" && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-amber-500 text-white rounded-sm" title={n.variance_message}>
                                  {n.variance_pct != null ? `${n.variance_pct}%` : "unusual"}
                                </span>
                              )}
                              {n.variance_severity === "OK" && (
                                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-sm">OK</span>
                              )}
                              {n.delta_since_previous_l != null && (
                                <div className="text-[10px] text-slate-500 mt-0.5">Δ {n.delta_since_previous_l.toFixed(2)} L since last close</div>
                              )}
                              {n.variance_message && (
                                <div className="text-[10px] text-red-700 mt-0.5">{n.variance_message}</div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Inject into Shift Entry -------------------------------- */}
                  <div className="mt-3 p-3 border-2 border-slate-900 bg-slate-50 rounded-sm space-y-2" data-testid="et-inject-panel">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                        One-tap inject into <b>{date} · {shiftCode}</b> shift entry
                      </div>
                      <div className="text-[11px] text-slate-600">
                        Pick <b>opening</b> if this is the start-of-shift slip, or <b>closing</b> for the end-of-shift slip. We create a DRAFT entry automatically if none exists yet.
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => injectEtReadings(etScan, "open")}
                        disabled={etScan.injecting}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="inject-open-btn"
                      >
                        Use as OPENING readings
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => injectEtReadings(etScan, "close")}
                        disabled={etScan.injecting}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid="inject-close-btn"
                      >
                        Use as CLOSING readings
                      </Button>
                      {etScan.injected && (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-2 py-1 rounded-sm" data-testid="et-inject-done">
                          <Check size={12} /> Injected {etScan.injected.updated} reading{etScan.injected.updated === 1 ? "" : "s"} — <a href="/shifts/new" className="underline">open Shift Entry</a>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-slate-600 mt-3 p-2 bg-amber-50 border border-amber-200 rounded-sm">
                    <b>VTOT is what we inject as the nozzle reading.</b> ATOT (₹ amount total) is not used. Variance chips compare against each nozzle&rsquo;s previous closing + 30-day average shift; anything red is worth eyeballing before submit.
                  </div>
                </>
              )}

              {!etBusy && etScan.result && etScan.result.status !== "OK" && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-300 rounded-sm p-3">
                  <div className="font-bold uppercase tracking-wider text-xs mb-1">{etScan.result.status}</div>
                  <div>{etScan.result.error || "Could not read enough of the slip. Please retake the photo with the whole slip in frame and better lighting."}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * FileCard — every uploaded file has:
 *   (a) a working "Attach to…" dropdown listing every checklist slot so the
 *       file can be bound in one click (bug 3.a);
 *   (b) an Eye button that opens the preview in a new tab (bug 3.b);
 *   (c) a Retake input to replace the photo (inherits binding).
 * The old inline "ET Scan" button was removed here per operator brief
 * bug 3.c (OCR paused, and the inline button belonged to Shift Entry
 * which is now typing-only).
 */
function FileCard({ file, date, shiftCode, onView, onRetake, onChanged }) {
  const [checklist, setChecklist] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!date || !shiftCode) return;
    api.get("/uploads/checklist", { params: { date, shift_code: shiftCode } })
       .then((r) => setChecklist(r.data?.items || []))
       .catch(() => {});
  }, [date, shiftCode, file.file_id]);

  const bind = async (key) => {
    if (!key) return;
    setBusy(true);
    try {
      const item = checklist.find((c) => c.key === key);
      await api.post("/uploads/checklist/bind", {
        date, shift_code: shiftCode, key,
        file_id: file.file_id, whole_day: !!item?.whole_day,
      });
      toast.success("Attached ✓");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const unbind = async () => {
    if (!file.checklist_key) return;
    setBusy(true);
    try {
      await api.post("/uploads/checklist/unbind", {
        date, shift_code: shiftCode, key: file.checklist_key, file_id: file.file_id,
      });
      toast.success("Unattached");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const boundItem = file.checklist_key ? checklist.find((c) => c.key === file.checklist_key) : null;

  return (
    <div data-testid={`file-card-${file.file_id}`} className="bg-white border-2 border-slate-300 rounded-sm p-2">
      <div className="text-xs font-mono truncate" title={file.original_name || file.file_id}>{file.original_name || file.file_id}</div>
      <div className="text-[10px] mt-0.5">
        {file.checklist_key ? (
          <span className="text-emerald-700 font-bold truncate" data-testid={`file-attached-${file.file_id}`}>
            ✓ Attached: {boundItem?.label || file.checklist_key}
          </span>
        ) : (
          <span className="text-amber-700 font-bold" data-testid={`file-unattached-${file.file_id}`}>Unattached — pick a slot below</span>
        )}
      </div>

      {/* Attach-to dropdown — grouped by bucket. This is the primary bind
          affordance now (bug 3.a). */}
      {!file.checklist_key && (
        <select
          disabled={busy || !checklist.length}
          onChange={(e) => bind(e.target.value)}
          className="w-full h-8 mt-1 border-2 border-slate-400 rounded-sm text-xs bg-white"
          data-testid={`attach-select-${file.file_id}`}
          defaultValue=""
        >
          <option value="">— Attach to slot… —</option>
          {["Sales", "Payments", "Debtor slips (whole day)", "Nozzle Adjustments"].map((group) => {
            const rows = checklist.filter((c) => c.group === group && c.state === "MISSING");
            if (rows.length === 0) return null;
            return (
              <optgroup key={group} label={group}>
                {rows.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </optgroup>
            );
          })}
        </select>
      )}

      <div className="flex gap-1 mt-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={onView} className="text-xs h-7 border-slate-300" data-testid={`view-file-${file.file_id}`}>
          <Eye size={12} className="mr-0.5" />Open
        </Button>
        {file.checklist_key && (
          <Button size="sm" variant="outline" onClick={unbind} disabled={busy}
                  className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                  data-testid={`unbind-file-${file.file_id}`}>
            Unattach
          </Button>
        )}
        <label className="inline-flex items-center gap-0.5 text-xs h-7 px-2 border border-amber-400 text-amber-700 bg-white rounded-sm cursor-pointer hover:bg-amber-50"
               title="Retake / replace this photo" data-testid={`retake-${file.file_id}`}>
          <Camera size={12} />Retake
          <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
                 onChange={(e) => { const nf = e.target.files?.[0]; e.target.value = ""; if (nf) onRetake(nf); }} />
        </label>
      </div>
    </div>
  );
}

